import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  type AuditStatusConfig,
  useUpdateAuditStatusConfig,
} from "@/hooks/use-audit-status-configs";
import { cn } from "@/lib/utils";
import { STATUS_COLORS } from "./status-colors";

const editSchema = z.object({
  label: z.string().min(1, "Label is required").max(50, "Label must be 50 characters or fewer"),
  color: z.string().min(1, "Color is required"),
});

type EditValues = z.infer<typeof editSchema>;

interface EditAuditStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: AuditStatusConfig;
}

export function EditAuditStatusDialog({ open, onOpenChange, config }: EditAuditStatusDialogProps) {
  const update = useUpdateAuditStatusConfig();

  const form = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: { label: config.label, color: config.color },
  });

  function onSubmit(values: EditValues) {
    update.mutate(
      { id: config.id, ...values },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Status</DialogTitle>
          <DialogDescription>
            Update the label and color for <span className="font-mono text-xs">{config.key}</span>.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Label</FormLabel>
                  <FormControl>
                    <Input placeholder="Status label" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <FormControl>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_COLORS.map((c) => (
                        <button
                          key={c.value}
                          type="button"
                          title={c.label}
                          onClick={() => field.onChange(c.value)}
                          className={cn(
                            "h-7 w-7 rounded-full ring-offset-background transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                            field.value === c.value
                              ? "ring-2 ring-foreground ring-offset-2 scale-110"
                              : "hover:scale-105",
                          )}
                          style={{ backgroundColor: c.value }}
                        />
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
